/**
 * MeetingAgendaDialog — ordre du jour de réunion commerciale généré
 * depuis les chiffres du pilotage (POST /pilotage/meeting-agenda).
 * Lot 4 : chaque décision peut devenir une action assignée à un agent.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ClipboardList, Copy, Loader2, Sparkles, Plus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function MeetingAgendaDialog({ open, onClose, period, onActionCreated }) {
    const [agenda, setAgenda] = useState(null);
    const [loading, setLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const [formKey, setFormKey] = useState(null);
    const [agentId, setAgentId] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState({});

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setAgenda(null);
        setFormKey(null);
        setCreated({});
        api.post(`/pilotage/meeting-agenda?period=${period === 'year' ? 'quarter' : period}`)
            .then((r) => setAgenda(r.data))
            .catch(() => toast.error("Impossible de générer l'ordre du jour"))
            .finally(() => setLoading(false));
        api.get('/pilotage/agents')
            .then((r) => setAgents(r.data.agents || []))
            .catch(() => {});
    }, [open, period]);

    const copyAll = () => {
        if (!agenda) return;
        const text = [`ORDRE DU JOUR — Réunion commerciale ${agenda.agency || ''}`,
            `Période : ${agenda.date_from?.slice(0, 10)} → ${agenda.date_to?.slice(0, 10)}`, '']
            .concat(agenda.sections.flatMap((s) => [s.title, ...s.items.map((i) => `  • ${i}`), '']))
            .join('\n');
        navigator.clipboard.writeText(text);
        toast.success('Ordre du jour copié');
    };

    const openForm = (key) => {
        setFormKey(formKey === key ? null : key);
        setAgentId('');
        setDueDate('');
    };

    const createAction = async (item, key) => {
        if (!agentId) { toast.error('Choisissez un agent'); return; }
        setBusy(true);
        try {
            await api.post('/pilotage/actions', {
                title: item.slice(0, 300), agent_id: agentId,
                due_date: dueDate ? `${dueDate}T12:00:00+00:00` : null,
                source: 'meeting',
            });
            setCreated((c) => ({ ...c, [key]: true }));
            setFormKey(null);
            toast.success('Action créée et agent notifié');
            onActionCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Création impossible');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto"
                           data-testid="meeting-agenda-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" style={{ color: '#681793' }} />
                        Ordre du jour — réunion commerciale
                    </DialogTitle>
                    <DialogDescription>
                        Généré depuis les chiffres du pilotage (source unique, chiffres sourcés).
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex flex-col items-center gap-2 py-10">
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#681793' }} />
                        <p className="text-xs text-muted-foreground">Analyse des chiffres de la période…</p>
                    </div>
                ) : agenda ? (
                    <>
                        <div className="space-y-4" data-testid="meeting-agenda-content">
                            {agenda.sections.map((s, i) => {
                                const isDecisions = /décision|action/i.test(s.title);
                                return (
                                    <div key={i}>
                                        <h3 className="text-sm font-bold mb-1.5" style={{ color: '#681793' }}>
                                            {s.title}
                                        </h3>
                                        <ul className="space-y-1">
                                            {s.items.map((item, j) => {
                                                const key = `${i}-${j}`;
                                                return (
                                                    <li key={j} className="text-sm">
                                                        <div className="flex gap-2 items-start">
                                                            <span style={{ color: '#F5B301' }}>•</span>
                                                            <span className="flex-1">{item}</span>
                                                            {isDecisions && (created[key] ? (
                                                                <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold shrink-0"
                                                                      data-testid={`agenda-action-created-${key}`}>
                                                                    <CheckCircle2 className="w-3.5 h-3.5" /> Action créée
                                                                </span>
                                                            ) : (
                                                                <button type="button" onClick={() => openForm(key)}
                                                                        data-testid={`agenda-action-btn-${key}`}
                                                                        className="flex items-center gap-0.5 text-[10px] font-semibold shrink-0 px-1.5 py-0.5 rounded-md border"
                                                                        style={{ borderColor: '#681793', color: '#681793' }}>
                                                                    <Plus className="w-3 h-3" /> Action
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {formKey === key && (
                                                            <div className="flex gap-1.5 mt-1.5 ml-4 flex-wrap"
                                                                 data-testid={`agenda-action-form-${key}`}>
                                                                <select value={agentId}
                                                                        onChange={(e) => setAgentId(e.target.value)}
                                                                        data-testid="agenda-action-agent-select"
                                                                        className="px-2 py-1 rounded-md border text-xs bg-transparent"
                                                                        style={{ borderColor: 'var(--border)' }}>
                                                                    <option value="">Assigner à…</option>
                                                                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                                                </select>
                                                                <input type="date" value={dueDate}
                                                                       onChange={(e) => setDueDate(e.target.value)}
                                                                       data-testid="agenda-action-due-input"
                                                                       className="px-2 py-1 rounded-md border text-xs bg-transparent"
                                                                       style={{ borderColor: 'var(--border)' }} />
                                                                <button type="button" disabled={busy}
                                                                        onClick={() => createAction(item, key)}
                                                                        data-testid="agenda-action-submit-btn"
                                                                        className="px-2.5 py-1 rounded-md text-xs font-bold text-white disabled:opacity-50"
                                                                        style={{ background: '#681793' }}>
                                                                    {busy ? '…' : 'Créer'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t"
                             style={{ borderColor: 'var(--border)' }}>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                {agenda.generated_by === 'llm' && <Sparkles className="w-3 h-3" style={{ color: '#F5B301' }} />}
                                {agenda.generated_by === 'llm' ? 'Rédigé par IA sur chiffres vérifiés' : 'Gabarit chiffré'}
                                {' · '}Sources : {agenda.sources?.finance || 'pilotage'}
                            </span>
                            <button type="button" onClick={copyAll}
                                    data-testid="meeting-agenda-copy-btn"
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                                    style={{ background: '#681793' }}>
                                <Copy className="w-3.5 h-3.5" /> Copier
                            </button>
                        </div>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
